// src/App.js
import { useState, useEffect } from 'react';
import './App.css';

function App() {
  const [now, setNow] = useState(new Date());
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isSynced, setIsSynced] = useState(false);
  const [showSeconds, setShowSeconds] = useState(true);
  const [showDate, setShowDate] = useState(false);
  const [timeColor, setTimeColor] = useState('#00ff00');
  const [fontFamily, setFontFamily] = useState('PT Sans');
  const [sizePreset, setSizePreset] = useState('large');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [timezone, setTimezone] = useState('America/Chicago');
  const [customOffset, setCustomOffset] = useState(0);

  // build the zone string
  const apiTimezone =
    timezone !== 'custom'
      ? timezone
      : `Etc/GMT${customOffset >= 0 ? '-' + customOffset : '+' + Math.abs(customOffset)}`;

  // fetch raw UTC once
  useEffect(() => {
    setLoading(true);
    setIsSynced(false);
    fetch('http://worldtimeapi.org/api/timezone/Etc/UTC')
      .then(r => {
        if (!r.ok) throw new Error('bad response');
        return r.json();
      })
      .then(data => {
        // data has utc_datetime (ISO8601 string) and unixtime
        const serverUtcMs = data.unixtime * 1000;
        setOffset(serverUtcMs - Date.now());
        setIsSynced(true);
      })
      .catch(err => {
        console.warn('sync failed, using local clock', err);
        setOffset(0);
        setIsSynced(false);
      })
      .finally(() => setLoading(false));
  }, []);

  // tick every second with that offset
  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date(Date.now() + offset));
    }, 1000);
    return () => clearInterval(id);
  }, [offset]);

  // format time 12h
  const timeString = now.toLocaleTimeString('en-US', {
    timeZone: apiTimezone,
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
    second: showSeconds ? '2-digit' : undefined,
  });

  // format date
  const rawDate = now.toLocaleDateString('en-US', {
    timeZone: apiTimezone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const dateString = rawDate.replace(/, (\d{4})$/, ' $1');

  const sizeMap = {
    small: '4rem',
    medium: '8rem',
    large: '12rem',
    xl: '15rem',
    xxl: '20rem',
  };

  return (
    <div className="app-container">
      <button className="settings-toggle" onClick={() => setSettingsOpen(s => !s)}>
        ⚙️
      </button>

      <div
        className="clock-display"
        style={{
          color: timeColor,
          fontFamily,
          fontSize: sizeMap[sizePreset],
        }}
      >
        {timeString}
      </div>

      {loading ? (
        <div className="sync-message">syncing time...</div>
      ) : !isSynced ? (
        <div className="sync-message">unsynced, using local clock</div>
      ) : null}

      {showDate && <div className="date-display">{dateString}</div>}

      <div className={`settings-panel ${settingsOpen ? 'open' : ''}`}>
        <label>
          pick tz:
          <select value={timezone} onChange={e => setTimezone(e.target.value)}>
            <option value="America/Los_Angeles">pacific (us)</option>
            <option value="America/Denver">mountain (us)</option>
            <option value="America/Chicago">central (us)</option>
            <option value="America/New_York">eastern (us)</option>
            <option value="Europe/London">london (uk)</option>
            <option value="Asia/Tokyo">tokyo (jp)</option>
            <option value="Etc/UTC">utc</option>
            <option value="custom">custom utc-n</option>
          </select>
        </label>

        {timezone === 'custom' && (
          <label>
            offset:
            <input
              type="number"
              value={customOffset}
              onChange={e => setCustomOffset(+e.target.value)}
              min="-12"
              max="14"
              step="1"
            />
            hrs
          </label>
        )}

        <label>
          <input
            type="checkbox"
            checked={showSeconds}
            onChange={e => setShowSeconds(e.target.checked)}
          />
          show seconds
        </label>
        <label>
          <input
            type="checkbox"
            checked={showDate}
            onChange={e => setShowDate(e.target.checked)}
          />
          show date
        </label>
        <label>
          pick color:
          <input type="color" value={timeColor} onChange={e => setTimeColor(e.target.value)} />
        </label>
        <label>
          pick font:
          <select value={fontFamily} onChange={e => setFontFamily(e.target.value)}>
            <option>PT Sans</option>
            <option>Roboto Mono</option>
            <option>Courier New</option>
          </select>
        </label>
        <label>
          size:
          <select value={sizePreset} onChange={e => setSizePreset(e.target.value)}>
            <option value="small">small</option>
            <option value="medium">medium</option>
            <option value="large">large</option>
            <option value="xl">xl</option>
            <option value="xxl">xxl</option>
          </select>
        </label>
      </div>
    </div>
  );
}

export default App;
